import { ContactItem } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.state'
import { BlocksView } from '@modules/Nzo.CMS/Frontend/libraries/cmsPage/blocks/blocks.template'
import { Img } from '@shared/Frontend/components/img'
import { useInView } from '@shared/Frontend/hooks/useInView'
import { pushDatalayer } from '@shared/Frontend/seo/datalayer'
import { cn } from '@shared/Frontend/utils/addToClassName'
import { isMobileNumber, whatsappFriendlyPhone } from '@shared/Frontend/utils/phone'
import { slugify } from '@shared/Frontend/utils/slugify'
import React from 'react'
import { BlockActions } from '../shared/blockActions'
import { BlockHeader } from '../shared/blockHeader'

export const ContactGridBlock: BlocksView['ContactGridBlock'] = (props) => {
  const { id, title, items } = props
  if (items?.length == 0) return <></>

  const { inView, ref } = useInView({ threshold: 0.1 })

  const subHeading = title ? 'h3' : 'h2'
  return (
    <section id={slugify(id)} ref={ref} className={cn('block block--contactgrid ls-spacing', inView && 'inview')}>
      <div className="block__inner ls-container ls-gutter">
        <div className="block__layout">
          <div className="block__content">
            <BlockHeader {...props} />

            <div className="block__content">
              <ListLayout items={items} subHeading={subHeading} listClassName="block__list" />
            </div>

            <BlockActions {...props} datalayerCategory="contactgrid_block" />
          </div>
        </div>
      </div>
    </section>
  )
}

export const ListLayout = (props: { items: ContactItem[]; subHeading: 'h2' | 'h3'; listClassName?: string }) => {
  const { items, subHeading, listClassName } = props
  return (
    <ul className={`contact ${listClassName || ''}`}>
      {items.map((item, index) => {
        return <ItemLayout key={index} {...item} subHeading={subHeading} />
      })}
    </ul>
  )
}

const ItemLayout = (props: { subHeading: 'h2' | 'h3' } & ContactItem) => {
  const {
    image,
    personName,
    personEmail,
    personFunction,
    personWebsite,
    personFacebook,
    personLinkedIn,
    personInstagram,
    personTwitter,
    personPhone,
    subHeading,
  } = props
  const SubHeading = subHeading
  const { inView, ref } = useInView<HTMLLIElement>({ threshold: 0.2 })
  return (
    <li ref={ref} className={cn('contact__item', inView && 'inview')}>
      <article className="contact__inner">
        <div className="contact__visual">
          {image && <Img {...image} loading="lazy" className="contact__image block__image" width="500" height="500" />}
        </div>
        <header className="contact__header">
          <SubHeading className="contact__title">{personName}</SubHeading>
          {personFunction && <p className="contact__description">{personFunction}</p>}
        </header>

        {(personEmail ||
          personWebsite ||
          personFacebook ||
          personLinkedIn ||
          personInstagram ||
          personTwitter ||
          personPhone) && (
          <ul className="contact__socials socials">
            {personEmail && <ContactSocialItem kind="Email" href={`mailto:${personEmail}`} label={personEmail} />}
            {personPhone && <ContactSocialItem kind="Phone" href={`tel:${personPhone}`} label={personPhone} />}
            {personWebsite && <ContactSocialItem kind="Website" href={personWebsite} />}
            {personFacebook && <ContactSocialItem kind="Facebook" href={personFacebook} />}
            {personLinkedIn && <ContactSocialItem kind="LinkedIn" href={personLinkedIn} />}
            {personInstagram && <ContactSocialItem kind="Instagram" href={personInstagram} />}
            {personTwitter && <ContactSocialItem kind="Twitter" href={personTwitter} />}
            {personPhone && isMobileNumber(personPhone) && (
              <ContactSocialItem kind="WhatsApp" href={`https://wa.me/${whatsappFriendlyPhone(personPhone)}`} />
            )}
          </ul>
        )}
      </article>
    </li>
  )
}

const ContactSocialItem = (props: {
  kind: 'Email' | 'Phone' | 'Website' | 'LinkedIn' | 'Facebook' | 'Twitter' | 'WhatsApp' | 'Instagram'
  href: string
  label?: string
}) => {
  return (
    <li className="socials__item">
      <a
        className="socials__clickable"
        href={props.href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          pushDatalayer({ category: 'contact_block', label: props.href, action: `${props.kind.toLowerCase()}_click` })
        }
      >
        <span className="socials__clickableLabel">{props.label || props.kind}</span>
      </a>
    </li>
  )
}
